import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "./AuthContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import ForgotCredentialsModal from "./components/ForgotCredentialsModal";

export default function Login() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, isAuthenticated } = useAuth();
  const [formData, setFormData] = useState({
    firstname: "",
    lastname: "",
    contractNumber: "",
  });
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showForgotModal, setShowForgotModal] = useState(false);
  const sessionExpired = searchParams.get("error") === "expired";

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate("/dashboard", { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const fillWithTestData = () => {
    setFormData({
      firstname: "Corentin",
      lastname: "Loriot",
      contractNumber: "VUJ-250616120922-DXTH",
    });
    setError("");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    try {
      await login(
        formData.firstname.trim(),
        formData.lastname.trim(),
        formData.contractNumber.trim()
      );
      // Navigation is handled by the auth context
    } catch (err) {
      setError(err instanceof Error ? err.message : "Une erreur est survenue");
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <div
      className="tw-min-h-screen tw-bg-cover tw-bg-no-repeat tw-bg-fixed"
      style={{
        backgroundImage: "url('/images/bg_mbclient_4_v2.webp')",
        backgroundPositionY: "-44px",
      }}
    >
      <div className="tw-container tw-mx-auto tw-px-4 tw-pt-8">
        {/* Logo and Welcome Section */}
        <div className="tw-flex tw-flex-col tw-items-center tw-pt-3 tw-pb-5">
          {/* Session expired alert */}
          {sessionExpired && (
            <div className="tw-absolute tw-top-0 tw-mt-2 tw-border-orange-600 tw-bg-orange-500 tw-text-white tw-w-11/12 md:tw-w-1/2 tw-rounded tw-px-5 tw-py-3 tw-shadow-lg">
              <div className="tw-flex tw-items-center">
                <svg className="tw-w-5 tw-h-5 tw-mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <span className="tw-font-medium">Votre session a expiré. Veuillez vous reconnecter.</span>
              </div>
            </div>
          )}

          {/* Login error alert */}
          {error && (
            <div className="tw-absolute tw-top-0 tw-mt-2 tw-border-red-600 tw-bg-red-600 tw-text-white tw-w-11/12 md:tw-w-1/2 tw-rounded tw-px-5 tw-py-3">
              {error}
            </div>
          )}

          {/* Affinicar Logo SVG */}
          <svg width="445" height="101" viewBox="0 0 445 101" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M334.321 60.0446C328.466 54.1417 322.729 53.0586 318.83 53.0586C312.844 53.0586 309.063 55.6039 306.727 58.0273C304.39 60.4508 302.04 64.7426 302.04 71.0517C302.04 77.3608 304.508 82.0588 306.989 84.6177C309.851 87.4337 313.894 89.1803 318.581 89.1803C323.793 89.1803 329.91 87.0276 334.334 81.9234V96.5589C331.21 98.5762 326.129 100.986 317.662 100.986C308.551 100.986 302.04 98.3055 296.829 92.6598C292.916 88.5033 289.018 81.788 289.018 71.4443C289.018 61.1006 293.31 53.722 297.354 49.552C301.646 45.1248 308.551 41.2256 319.356 41.2256C323.517 41.2256 328.466 41.9025 334.334 45.2602V60.0311L334.321 60.0446Z" fill="white"/>
            <path d="M383.404 42.715H396.164V99.2533H383.404V93.3504C377.812 100.066 370.907 101 366.995 101C350.073 101 340.438 86.4997 340.438 70.9165C340.438 52.5171 352.672 41.2393 367.126 41.2393C371.156 41.2393 378.193 42.3088 383.404 49.2949V42.715ZM353.46 71.1872C353.46 80.9894 359.446 89.1804 368.701 89.1804C376.775 89.1804 384.192 83.1421 384.192 71.3226C384.192 59.5032 376.775 53.0587 368.701 53.0587C359.459 53.0587 353.46 61.1143 353.46 71.1872Z" fill="white"/>
            <path d="M409.057 42.715H421.817V47.8191C423.248 46.0726 425.335 44.1907 427.16 43.1212C429.628 41.6454 432.109 41.2393 434.971 41.2393C438.095 41.2393 441.482 41.7808 445 43.92L439.788 55.8748C436.927 53.9929 434.577 53.8575 433.277 53.8575C430.547 53.8575 427.803 54.2637 425.335 56.9443C421.817 60.8435 421.817 66.2049 421.817 69.9687V99.2398H409.057V42.715Z" fill="white"/>
            <path d="M95.3328 99.2534L66.4258 34.3887L95.3328 99.2534Z" fill="white"/>
            <path d="M0 55.0625L16.2914 55.252L30.6267 82.939L23.1834 99.2534L0 55.0625Z" fill="white"/>
            <path d="M66.4258 34.3884V4.44043L109.655 99.2531H95.3328L66.4258 34.3884Z" fill="white"/>
            <path d="M37.5195 99.2531H23.1973L66.4265 4.44043L73.1872 19.2384L37.5195 99.2531Z" fill="white"/>
            <path d="M233.816 41.2391C230.692 41.2391 224.443 41.6453 218.969 47.9544V42.7149H206.209V99.2532H218.969V72.1213C218.969 53.8574 224.824 53.045 230.561 53.045C235.379 53.045 240.59 53.4512 240.59 67.8159V99.2396H253.35V64.5937C253.35 55.8611 252.051 51.9754 249.963 48.875C248.27 46.4516 243.977 41.2256 233.816 41.2256V41.2391Z" fill="white"/>
            <path d="M270.415 11.1425C267.58 11.7924 265.283 14.1617 264.652 17.0861C263.208 23.7879 268.315 29.0409 274.8 27.5517C277.636 26.9018 279.933 24.519 280.563 21.6081C282.007 14.9199 276.9 9.65327 270.415 11.1425Z" fill="white"/>
            <path d="M278.989 42.7148H266.229V99.2532H278.989V42.7148Z" fill="white"/>
            <path d="M161.417 42.9724V25.6426C161.417 20.2677 162.323 17.9932 162.979 16.91C164.279 14.4866 166.76 12.6182 171.053 12.6182C173.363 12.6182 175.608 13.1462 177.407 14.7709L183.052 2.38284C180.216 1.00188 176.357 0 171.053 0C160.511 0 154.906 4.5626 152.044 8.73257C148.789 13.5659 148.657 18.8055 148.657 24.1804V42.9859H130.489H130.646V23.0567C130.646 19.7397 131.604 17.7901 132.116 16.9371C133.416 14.5137 135.897 12.6453 140.19 12.6453C141.883 12.6453 143.327 12.8078 144.47 13.4576C145.651 8.85442 147.935 4.9823 150.902 2.437C148.158 1.01542 145.598 0.0270777 140.19 0.0270777C129.648 0.0270777 124.043 4.58968 121.181 8.75965C118.083 13.3493 117.82 18.3046 117.794 23.3952V42.9994H99.0742L104.588 55.0897H117.794V99.5378H130.554V55.0897H148.644V99.5378H161.404V55.0897H180.675V99.5378H193.436V42.9724H161.404H161.417Z" fill="white"/>
          </svg>

          <div className="tw-mx-auto tw-text-center tw-text-white tw-text-5xl tw-font-medium tw-mt-8">
            Bienvenue sur votre espace
          </div>
        </div>

        {/* Form Container */}
        <div className="tw-max-w-5xl tw-mx-auto tw-mb-20">
          <div className="tw-rounded-2xl tw-overflow-hidden">
            {/* Header */}
            <div
              className="tw-text-white tw-p-4 tw-text-center"
              style={{
                background: "linear-gradient(90deg, #575FA7 0%, #8074AF 100%)",
              }}
            >
              <p className="tw-font-medium tw-mb-3 tw-text-2xl">
                Vous êtes bénéficiaire d'un contrat panne mécanique automobile.
              </p>
              <p className="tw-text-2xl tw-font-normal">
                Cet espace vous permet de déclarer votre panne et de suivre l'avancée de votre dossier.
              </p>
              <p className="tw-font-light tw-mt-4 tw-text-lg">Besoin d'aide, une question ?</p>
              <p className="tw-font-light tw-text-lg">
                contactez nos gestionnaires au 0 800 944 665 (appel gratuit).
              </p>
            </div>

            {/* Form */}
            <div className="tw-bg-white tw-p-8">
              <div className="tw-text-center tw-text-3xl tw-font-medium tw-text-black tw-mb-6">
                ME CONNECTER
              </div>

              {/* Bouton données de test en développement */}
              {process.env.NODE_ENV === 'development' && (
                <div className="tw-flex tw-justify-end tw-mb-4">
                  <button
                    type="button"
                    onClick={fillWithTestData}
                    className="tw-text-sm tw-text-blue-600 hover:tw-text-blue-800 tw-underline tw-flex tw-items-center tw-gap-2 tw-p-2 tw-rounded-lg hover:tw-bg-blue-50"
                  >
                    <FontAwesomeIcon icon={faInfoCircle} className="tw-text-sm" />
                    <span>Utiliser les données de test</span>
                  </button>
                </div>
              )}

              <form onSubmit={handleSubmit}>
                <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-6 tw-max-w-4xl tw-mx-auto">
                  <div className="tw-form-group">
                    <label
                      htmlFor="firstname"
                      className="tw-text-gray-700 tw-text-lg tw-font-light tw-mb-2 tw-block"
                    >
                      Prénom <em className="tw-text-red-600">*</em>
                    </label>
                    <input
                      type="text"
                      className="tw-w-full tw-rounded-xl tw-bg-gray-100 tw-px-4 tw-py-3 tw-text-gray-700 tw-text-base tw-font-light tw-outline-none tw-focus:ring-2 tw-focus:ring-purple-500"
                      id="firstname"
                      name="firstname"
                      value={formData.firstname}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div className="tw-form-group">
                    <label
                      htmlFor="lastname"
                      className="tw-text-gray-700 tw-text-lg tw-font-light tw-mb-2 tw-block"
                    >
                      Nom de famille <em className="tw-text-red-600">*</em>
                    </label>
                    <input
                      type="text"
                      className="tw-w-full tw-rounded-xl tw-bg-gray-100 tw-px-4 tw-py-3 tw-text-gray-700 tw-text-base tw-font-light tw-outline-none tw-focus:ring-2 tw-focus:ring-purple-500"
                      id="lastname"
                      name="lastname"
                      value={formData.lastname}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div className="tw-form-group">
                    <label
                      htmlFor="contractNumber"
                      className="tw-text-gray-700 tw-text-lg tw-font-light tw-mb-2 tw-block"
                    >
                      Numéro de contrat <em className="tw-text-red-600">*</em>
                    </label>
                    <input
                      type="text"
                      className="tw-w-full tw-rounded-xl tw-bg-gray-100 tw-px-4 tw-py-3 tw-text-gray-700 tw-text-base tw-font-light tw-outline-none tw-focus:ring-2 tw-focus:ring-purple-500"
                      id="contractNumber"
                      name="contractNumber"
                      value={formData.contractNumber}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>

                <div className="tw-mt-2 tw-text-right">
                  <span className="tw-text-red-600 tw-text-sm">* Champs obligatoires</span>
                </div>

                <div className="tw-flex tw-justify-center tw-mt-6">
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="tw-transform -tw-skew-x-12 tw-px-12 tw-py-3 tw-text-white tw-uppercase tw-tracking-wider tw-transition-all tw-duration-300 hover:tw-opacity-90 disabled:tw-opacity-60"
                    style={{
                      background: "linear-gradient(90deg, #575FA7 0%, #8074AF 100%)",
                    }}
                  >
                    <div className="tw-transform tw-skew-x-12">
                      {isLoading ? "Connexion..." : "Se connecter"}
                    </div>
                  </button>
                </div>

                {/* Lien oubli identifiants */}
                <div className="tw-text-center tw-mt-4">
                  <button
                    type="button"
                    onClick={() => setShowForgotModal(true)}
                    className="tw-text-purple-600 hover:tw-text-purple-800 tw-underline tw-text-sm tw-font-medium"
                  >
                    J'ai oublié mes identifiants de connexion
                  </button>
                </div>
              </form>
            </div>

            {/* Bottom Section */}
            <div className="tw-bg-gray-100 tw-p-8">
              {/* Stepper */}
              <div className="tw-bg-white tw-rounded-2xl tw-p-8 tw-mb-8">
                <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-5 tw-gap-5 tw-items-center">
                  {/* Step 1 */}
                  <div className="tw-flex tw-flex-col tw-items-center">
                    <div
                      className="tw-w-8 tw-h-8 tw-rounded-full tw-text-white tw-flex tw-items-center tw-justify-center tw-text-xl tw-mb-4"
                      style={{
                        background: "linear-gradient(90deg, #575FA7 0%, #8074AF 100%)",
                      }}
                    >
                      1
                    </div>
                    <div className="tw-text-center tw-text-lg">
                      Confirmez <b>vos informations personnelles.</b>
                    </div>
                  </div>

                  {/* Separator */}
                  <div className="tw-hidden md:tw-flex tw-justify-center">
                    <svg xmlns="http://www.w3.org/2000/svg" width="39" height="60" viewBox="0 0 39 60" fill="none">
                      <path d="M16.1347 59.0943L37.1484 29.9983L16.1347 0.902344" stroke="url(#paint0_linear_948_2377)" strokeWidth="2"/>
                      <path d="M8.59169 59.0943L29.6055 29.9983L8.5917 0.902344" stroke="url(#paint1_linear_948_2377)" strokeWidth="2"/>
                      <path d="M0.999897 59.0943L22.0137 29.9983L0.999899 0.902344" stroke="url(#paint2_linear_948_2377)" strokeWidth="2"/>
                      <defs>
                        <linearGradient id="paint0_linear_948_2377" x1="37.1484" y1="29.9983" x2="16.1347" y2="29.9983" gradientUnits="userSpaceOnUse">
                          <stop stopColor="#575FA7"/>
                          <stop offset="1" stopColor="#575FA7" stopOpacity="0"/>
                        </linearGradient>
                        <linearGradient id="paint1_linear_948_2377" x1="29.6055" y1="29.9983" x2="8.59169" y2="29.9983" gradientUnits="userSpaceOnUse">
                          <stop stopColor="#575FA7"/>
                          <stop offset="1" stopColor="#575FA7" stopOpacity="0"/>
                        </linearGradient>
                        <linearGradient id="paint2_linear_948_2377" x1="22.0137" y1="29.9983" x2="0.999892" y2="29.9983" gradientUnits="userSpaceOnUse">
                          <stop stopColor="#575FA7"/>
                          <stop offset="1" stopColor="#575FA7" stopOpacity="0"/>
                        </linearGradient>
                      </defs>
                    </svg>
                  </div>

                  {/* Step 2 */}
                  <div className="tw-flex tw-flex-col tw-items-center">
                    <div
                      className="tw-w-8 tw-h-8 tw-rounded-full tw-text-white tw-flex tw-items-center tw-justify-center tw-text-xl tw-mb-4"
                      style={{
                        background: "linear-gradient(90deg, #575FA7 0%, #8074AF 100%)",
                      }}
                    >
                      2
                    </div>
                    <div className="tw-text-center tw-text-lg">
                      Transmettez-nous <b>la carte grise du véhicule.</b>
                    </div>
                  </div>

                  {/* Separator */}
                  <div className="tw-hidden md:tw-flex tw-justify-center">
                    <svg xmlns="http://www.w3.org/2000/svg" width="39" height="60" viewBox="0 0 39 60" fill="none">
                      <path d="M16.1347 59.0943L37.1484 29.9983L16.1347 0.902344" stroke="url(#paint0_linear_948_2377)" strokeWidth="2"/>
                      <path d="M8.59169 59.0943L29.6055 29.9983L8.5917 0.902344" stroke="url(#paint1_linear_948_2377)" strokeWidth="2"/>
                      <path d="M0.999897 59.0943L22.0137 29.9983L0.999899 0.902344" stroke="url(#paint2_linear_948_2377)" strokeWidth="2"/>
                      <defs>
                        <linearGradient id="paint0_linear_948_2377_2" x1="37.1484" y1="29.9983" x2="16.1347" y2="29.9983" gradientUnits="userSpaceOnUse">
                          <stop stopColor="#575FA7"/>
                          <stop offset="1" stopColor="#575FA7" stopOpacity="0"/>
                        </linearGradient>
                        <linearGradient id="paint1_linear_948_2377_2" x1="29.6055" y1="29.9983" x2="8.59169" y2="29.9983" gradientUnits="userSpaceOnUse">
                          <stop stopColor="#575FA7"/>
                          <stop offset="1" stopColor="#575FA7" stopOpacity="0"/>
                        </linearGradient>
                        <linearGradient id="paint2_linear_948_2377_2" x1="22.0137" y1="29.9983" x2="0.999892" y2="29.9983" gradientUnits="userSpaceOnUse">
                          <stop stopColor="#575FA7"/>
                          <stop offset="1" stopColor="#575FA7" stopOpacity="0"/>
                        </linearGradient>
                      </defs>
                    </svg>
                  </div>

                  {/* Step 3 */}
                  <div className="tw-flex tw-flex-col tw-items-center">
                    <div
                      className="tw-w-8 tw-h-8 tw-rounded-full tw-text-white tw-flex tw-items-center tw-justify-center tw-text-xl tw-mb-4"
                      style={{
                        background: "linear-gradient(90deg, #575FA7 0%, #8074AF 100%)",
                      }}
                    >
                      3
                    </div>
                    <div className="tw-text-center tw-text-lg">
                      Complétez <b>les informations relatives à votre panne.</b>
                    </div>
                  </div>
                </div>
              </div>

              {/* Information Section */}
              <div
                className="tw-rounded-3xl tw-p-6 tw-flex tw-flex-col md:tw-flex-row tw-justify-between tw-items-start tw-gap-6"
                style={{
                  background: "linear-gradient(90deg, #575FA7 0%, #8074AF 100%)",
                }}
              >
                <div className="tw-flex-1 tw-pb-6 md:tw-pb-16 md:tw-pr-20">
                  <div className="tw-text-white tw-text-3xl tw-font-normal tw-uppercase">
                    AFFINICAR
                  </div>
                  <div className="tw-text-white tw-text-3xl tw-font-normal tw-uppercase tw-mb-6">
                    votre seul interlocuteur
                  </div>

                  <div className="tw-text-white tw-text-lg tw-font-light tw-mt-4">
                    AFFINICAR a été missionné par votre partenaire automobile pour gérer votre panne et{" "}
                    <span className="tw-text-black tw-font-normal">
                      vous permettre de mettre en œuvre votre contrat de panne mécanique automobile dans les
                      meilleures conditions.
                    </span>
                  </div>
                  <div className="tw-text-white tw-text-lg tw-font-light tw-mt-4">
                    Une fois votre déclaration effectuée,{" "}
                    <span className="tw-text-black tw-font-normal">
                      nous vous guiderons au besoin vers un réparateur et nous analyserons votre panne.
                    </span>{" "}
                    Nous vous remettrons ensuite la décision de prise en charge après validation de votre
                    partenaire automobile.
                  </div>
                </div>

                <div className="tw-self-end">
                  <img
                    src="/images/affinicar-screen-iphone.png"
                    alt="Affinicar"
                    className="tw-w-64 tw-mr-5"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal d'oubli d'identifiants */}
      <ForgotCredentialsModal
        isOpen={showForgotModal}
        onClose={() => setShowForgotModal(false)}
      />
    </div>
  );
}